// planDisplay — derive the short display strings the swipe / pinned /
// compare cards expect from the real Plan shape.
//
// The reference mockup hand-types fields like dentalMax: "$2,500/yr",
// otcAllowance: 50, hearing: "Routine + aids". Real plans expose nested
// objects in `Plan.benefits.*`. This module is the single place that
// maps one to the other so every card renders consistently.

import type { OonCost, Plan } from '@/types/plans';
import { fitnessProgramForCarrier } from '@/lib/fitness-program';
import { sharedAllowanceSuffix } from '@/lib/extractBenefitValue';

export interface PlanDisplay {
  dental: string;
  dentalMax: string;
  vision: string;
  visionAllowance: string;
  hearing: string;
  otcText: string;          // formatted "$N/mo"
  otcMonthly: number;       // raw monthly $ for goodness comparisons
  meals: string;
  transport: string;
  fitness: string;
  ozempicMonthlyHint: string | null;  // optional rx hint for headers
}

function fmtMoney(n: number): string {
  return '$' + n.toLocaleString();
}

export function planDisplay(plan: Plan): PlanDisplay {
  const b = plan.benefits;
  // Dental: prefer the structured annual_max; fall back to a coverage
  // descriptor when the plan only filed a description string.
  const dental = b.dental.comprehensive
    ? 'Comprehensive'
    : b.dental.preventive
      ? 'Preventive only'
      : b.dental.description
        ? 'Per description'
        : 'None';
  const dentalMax = b.dental.annual_max > 0
    ? `${fmtMoney(b.dental.annual_max)}/yr`
    // No max filed: say what the plan covers. "No annual cap filed" on a
    // preventive-only plan read as unlimited dental (Rob, 2026-10-17).
    : b.dental.comprehensive
      ? 'No yearly max filed'
      : b.dental.preventive
        ? 'Preventive only'
        : 'None';

  // Allowance amount === 1 is the importer's "offered, no Medicare.gov-
  // published ceiling" sentinel (api/plans.ts transformPbpRow rescue,
  // commit d6a3952) — applies to vision_allowance, hearing_aid_allowance,
  // otc_allowance, and food_card. Surfacing the raw "$1" would read as
  // a real $1 cap; prefer the filed description, fall back to "Included".
  const visionEyewear = b.vision.eyewear_allowance_year;
  const visionDesc = b.vision.description?.trim();
  const vision = b.vision.exam
    ? visionEyewear > 0
      ? 'Routine + eyewear'
      : 'Exam only'
    : 'None';
  const visionAllowance = visionEyewear > 1
    ? `${fmtMoney(visionEyewear)}`
    : visionEyewear === 1
      ? (visionDesc || 'Included')
      : '$0';

  const hearingAids = b.hearing.aid_allowance_year;
  const hearingDesc = b.hearing.description?.trim();
  const hearingAidsLabel = hearingAids > 1
    ? `${fmtMoney(hearingAids)}/yr aids`
    : hearingAids === 1
      ? (hearingDesc || 'Included')
      : null;
  const hearing = hearingAidsLabel
    ? b.hearing.exam
      ? `Routine + ${hearingAidsLabel}`
      : hearingAidsLabel
    : b.hearing.exam
      ? 'Routine only'
      : 'None';

  // OTC pm_plan_benefits files quarterly; spec headers in $/mo.
  const otcQuarterly = b.otc.allowance_per_quarter;
  const otcDesc = b.otc.description?.trim();
  const otcMonthly = otcQuarterly > 1 ? Math.round(otcQuarterly / 3) : 0;
  const otcText = otcQuarterly > 1
    ? `$${otcMonthly}/mo${sharedAllowanceSuffix(otcDesc)}`
    : otcQuarterly === 1
      ? (otcDesc || 'Included')
      : '$0/mo';

  const foodMonthly = b.food_card.allowance_per_month;
  const foodDesc = b.food_card.description?.trim();
  const meals = foodMonthly > 1
    ? `$${foodMonthly}/mo${sharedAllowanceSuffix(foodDesc)}`
    : foodMonthly === 1
      ? (foodDesc || 'Included')
      : 'None';

  const transport = b.transportation.rides_per_year > 0
    ? `${b.transportation.rides_per_year} trips/yr`
    : 'None';

  // Brand the fitness benefit using the carrier heuristic — Humana →
  // Go365, UHC/AARP → Renew Active, BCBS → Silver&Fit, default →
  // SilverSneakers. Prefer the value already filed on the plan if it's
  // a real program name; "Yes" / null fall through to the heuristic so
  // the agent never sees a generic label.
  const filedProgram = b.fitness.program;
  const fitness = b.fitness.enabled
    ? filedProgram && filedProgram !== 'OneCall' && filedProgram !== 'Active&Fit'
      ? filedProgram
      : fitnessProgramForCarrier(plan.carrier)
    : 'None';

  return {
    dental,
    dentalMax,
    vision,
    visionAllowance,
    hearing,
    otcText,
    otcMonthly,
    meals,
    transport,
    fitness,
    ozempicMonthlyHint: null,
  };
}

// Annual = monthly premium × 12 + estimated annual drug cost. Drug
// cost is supplied by the caller (resolved upstream from useDrugCosts /
// usePlanBrain) so this function stays a pure formatter.
export function annualEstimate(plan: Plan, annualDrugCost: number | null): {
  total: number | null;
  premiumComponent: number;
  drugComponent: number | null;
} {
  const premiumComponent = plan.premium * 12;
  if (annualDrugCost == null) {
    return { total: null, premiumComponent, drugComponent: null };
  }
  return {
    total: premiumComponent + annualDrugCost,
    premiumComponent,
    drugComponent: annualDrugCost,
  };
}

export function formatPremium(plan: Plan): string {
  return plan.premium === 0 ? '$0' : `$${plan.premium}`;
}

// formatPcp / formatSpecialist used to read .copay only and return "—"
// when copay was null. That hid every coinsurance-only filing — UHC
// H5453-016 specialist files coinsurance=30 with copay=null and the
// Compare screen rendered "—" instead of "30%". Delegate to
// formatCostShare so the full copay → coinsurance → description ladder
// is honored.
export function formatPcp(plan: Plan): string {
  return formatCostShare(plan.benefits.medical.primary_care);
}

export function formatSpecialist(plan: Plan): string {
  return formatCostShare(plan.benefits.medical.specialist);
}

// Generic CostShare formatter — copay wins when present, otherwise
// coinsurance %, otherwise the benefit_description (for SNP / "covered"
// rows where CMS doesn't file a structured $), otherwise em-dash.
// PDP guard: when the column is a Part D drug-only plan, medical
// categories don't apply — render "N/A — Part D only" instead of "—".
export function formatCostShare(
  cs:
    | {
        copay: number | null;
        coinsurance: number | null;
        description?: string | null;
      }
    | undefined
    | null,
  opts?: { isPdp?: boolean },
): string {
  if (opts?.isPdp) return 'N/A — Part D only';
  if (!cs) return '—';
  if (cs.copay != null) return `$${cs.copay}`;
  if (cs.coinsurance != null) return `${cs.coinsurance}%`;
  if (cs.description) return cs.description;
  return '—';
}

// Range-aware CostShare formatter. Carriers commonly file ranged
// copays for advanced imaging / outpatient surgery / diagnostic
// procedures where the actual member cost depends on the specific
// CPT code; pm_plan_benefits.copay stores only the minimum of the
// range, but the benefit_description carries the full span ("MRI /
// CT / PET · $0–$325 copay"). Surfacing just "$0" misleads the
// broker into thinking the worst case is $0 too. When a parsable
// range is in the description AND its low end matches the stored
// copay, render the full "$LOW–$HIGH" instead.
const COST_RANGE_RE = /\$(\d[\d,]*)\s*[–\-—to]+\s*\$(\d[\d,]*)/i;

export function formatCostShareWithRange(
  cs:
    | {
        copay: number | null;
        coinsurance: number | null;
        description: string | null;
        copay_low?: number | null;
        copay_high?: number | null;
        coinsurance_low?: number | null;
        coinsurance_high?: number | null;
      }
    | undefined
    | null,
  opts?: { isPdp?: boolean },
): string {
  if (opts?.isPdp) return 'N/A — Part D only';
  if (!cs) return '—';
  // Prefer the structured same-benefit range (copay_low/copay_high from
  // the CMS PBP filing's copay/copay_max) over parsing the description.
  // Show "$low–$high" when they differ, single value otherwise.
  if (cs.copay_low != null && cs.copay_high != null && cs.copay_high > cs.copay_low) {
    return `$${cs.copay_low}–$${cs.copay_high}`;
  }
  if (
    cs.coinsurance_low != null &&
    cs.coinsurance_high != null &&
    cs.coinsurance_high > cs.coinsurance_low
  ) {
    return `${cs.coinsurance_low}%–${cs.coinsurance_high}%`;
  }
  if (cs.copay != null) {
    if (cs.description) {
      const m = cs.description.match(COST_RANGE_RE);
      if (m) {
        const low = parseInt(m[1].replace(/,/g, ''), 10);
        const high = parseInt(m[2].replace(/,/g, ''), 10);
        if (
          Number.isFinite(low) &&
          Number.isFinite(high) &&
          high > low &&
          // Match either endpoint. Two production shapes:
          //  1) cs.copay is the min (pm_plan_benefits row stored copay=5,
          //     max=$50; api/plans.ts returned copay=5 unpromoted).
          //  2) cs.copay is the max (row stored copay=0, max=$190; api/
          //     plans.ts's costShareFor promoted copay to $190 because
          //     the raw value was 0). Without accepting high === cs.copay
          //     the range collapses to "$190" flat even though the
          //     description carries the full "$0–$190" text.
          (low === cs.copay || high === cs.copay)
        ) {
          return `$${low}–$${high}`;
        }
      }
    }
    return `$${cs.copay}`;
  }
  if (cs.coinsurance != null) return `${cs.coinsurance}%`;
  if (cs.description) return cs.description;
  return '—';
}

export function costShareNumeric(
  cs: { copay: number | null; coinsurance: number | null } | undefined | null,
): number | null {
  if (!cs) return null;
  if (cs.copay != null) return cs.copay;
  if (cs.coinsurance != null) return cs.coinsurance;
  return null;
}

// ── Out-of-network line (CMS PBP Section C) ──────────────────────────
// Every value is the plan's own CMS filing (pm_plan_oon_costs). A plan with
// no Section C row for the year returns null → no line, never a guess.
const OON_LOADED_KEYS: ReadonlySet<string> = new Set([
  'primary_care', 'specialist', 'chiropractic', 'occupational_therapy',
  'physical_speech_therapy', 'podiatry', 'mental_health_individual',
  'mental_health_group', 'substance_abuse', 'home_health',
  'diagnostic_procedures', 'lab_services', 'advanced_imaging', 'xray',
  'outpatient_surgery_hospital', 'outpatient_observation',
  'outpatient_surgery_asc', 'ambulance', 'air_transportation',
  'dme_prosthetics', 'diabetic_supplies', 'renal_dialysis', 'partb_drugs',
  'inpatient', 'mental_health_inpatient', 'snf', 'dental_medicare_covered',
]);
// Same Plan-Finder / SBF convention as in-network: these headline the
// filed maximum, not the range floor.
const OON_HIGH_END_KEYS: ReadonlySet<string> = new Set([
  'ambulance', 'outpatient_surgery_asc', 'outpatient_surgery_hospital',
]);

function money(n: number): string {
  return n % 1 === 0 ? `$${n.toLocaleString()}` : `$${n.toFixed(2)}`;
}

export function formatOonCost(
  c: OonCost,
  key: string,
): string {
  if (c.original_medicare_cost_share && !c.ladder && c.copay_min == null && c.coins_min == null) {
    return 'Original Medicare amounts';
  }
  const parts: string[] = [];
  if (c.per_stay != null) parts.push(`${money(c.per_stay)} per stay`);
  if (c.ladder && c.ladder.length > 0) {
    parts.push(
      c.ladder
        .map((t) => {
          const v = t.copay != null ? `${money(t.copay)}/day` : `${t.coins}%`;
          return `${v} days ${t.from_day ?? '?'}–${t.to_day ?? '?'}`;
        })
        .join(' · '),
    );
  }
  const hi = OON_HIGH_END_KEYS.has(key);
  if (c.copay_min != null || c.copay_max != null) {
    const lo = c.copay_min ?? c.copay_max!;
    const up = c.copay_max ?? lo;
    parts.push(hi || up === lo ? money(up) : `${money(lo)}–${money(up)}`);
  }
  if (c.coins_min != null || c.coins_max != null) {
    const lo = c.coins_min ?? c.coins_max!;
    const up = c.coins_max ?? lo;
    parts.push(hi || up === lo ? `${up}%` : `${lo}%–${up}%`);
  }
  return parts.length > 0 ? parts.join(' or ') : '—';
}

/** Second line under an in-network cost cell: "Out-of-network: $50".
 *  null when the row has no out-of-network meaning or no data is loaded. */
export function formatOonLine(plan: Plan, key: string): string | null {
  const oon = plan.out_of_network;
  if (!oon) return null;
  if (key === 'emergency' || key === 'urgent_care') {
    return 'Out-of-network: same cost (covered anywhere)';
  }
  if (!OON_LOADED_KEYS.has(key)) return null;
  const label = oon.mode === 'pos' ? 'Out-of-network (POS)' : 'Out-of-network';
  const c = oon.mode === 'none' ? undefined : oon.costs[key as keyof typeof oon.costs];
  if (!c) {
    // Emergency ambulance is covered anywhere, at the in-network cost.
    if (key === 'ambulance' || key === 'air_transportation') {
      return 'Out-of-network: same cost in an emergency';
    }
    // Medicare-covered dental (B16a) only: on a PPO/POS plan, OON IS
    // covered — we just don't have the filed B16a dollar amount. Saying
    // "not covered" there would read as a coverage denial. Surface
    // "Not filed — call to verify" instead. On an in-network-only HMO
    // (mode 'none') OON genuinely isn't covered, so it falls through to
    // the generic "not covered" below. Dental-key only; siblings unchanged.
    if (key === 'dental_medicare_covered' && oon.mode !== 'none') {
      return 'Out-of-network: Not filed — call to verify';
    }
    return 'Out-of-network: not covered';
  }
  return `${label}: ${formatOonCost(c, key)}`;
}
